import { useAtomValue } from "jotai";

import {
  periodSchema,
  type HomeMetricsResponse,
  type Period,
} from "@go-novus/admin-api";
import {
  EmptyState,
  Icon,
  MetricCard,
  PageHeader,
  Panel,
  SegmentedControl,
  StatusMark,
  type IconName,
} from "@go-novus/ui";

import {
  homeMetricsAtom,
  readHomeMetricsFailureMessage,
} from "#/features/home-metrics";
import {
  readStoreConnectionFailureMessage,
  storeConnectionAtom,
} from "#/features/store-connection";
import * as m from "#/paraglide/messages";
import { formatWorkspaceTime } from "#/shared/lib/date";

import {
  formatCount,
  formatCountChange,
  formatRate,
  formatRateChange,
  readChangeTone,
  readInvertedChangeTone,
} from "../lib/format-metric";
import {
  ConversationTrend,
  ConversationTrendLoading,
} from "./conversation-trend";

const PERIOD_OPTIONS: { label: string; value: Period }[] = [
  { label: m.home_period_last_7_days(), value: "last_7_days" },
  { label: m.home_period_last_30_days(), value: "last_30_days" },
];

interface HomeNoticeProps {
  body?: string;
  heading: string;
  iconName?: IconName;
  note?: string;
}

function HomeNotice({
  body,
  heading,
  iconName = "inbox",
  note,
}: HomeNoticeProps) {
  return (
    <Panel>
      <EmptyState
        body={body}
        heading={heading}
        icon={<Icon name={iconName} />}
        note={note}
      />
    </Panel>
  );
}

interface EmptyNotice {
  body: string;
  heading: string;
  note?: string;
}

/** 无知识、未上线、还没有会话，三种情况都不摆一屏零，改摆一句话。 */
function readEmptyNotice(
  data: HomeMetricsResponse,
  storeName: string | undefined,
): EmptyNotice | null {
  if (data.emptyReason === "no_knowledge") {
    return {
      body: m.home_empty_no_knowledge_body(),
      heading: m.home_empty_no_knowledge_heading(),
    };
  }

  if (data.emptyReason === "storefront_not_live") {
    return {
      body: m.home_empty_storefront_not_live_body(),
      heading: m.home_empty_storefront_not_live_heading(),
    };
  }

  if (data.metrics.conversations.value === 0) {
    return {
      body: m.home_empty_no_conversations_body(),
      heading: m.home_empty_no_conversations_heading(),
      // 只有这一种空态能确定挂件已经上线，所以尾注也只在这里给。
      note:
        storeName === undefined
          ? undefined
          : m.home_empty_no_conversations_note({ store: storeName }),
    };
  }

  return null;
}

// 按内容区宽度分栏，不看视口（与 console-shell 的注释一致）：
// 每张卡低于 170px 标签就会碎成三行，所以 544px 以下先摞起来。
const METRIC_GRID = "grid grid-cols-1 gap-4 @min-[34rem]:grid-cols-3";

interface HomeMetricsBodyProps {
  data: HomeMetricsResponse | undefined;
  failureMessage: string | null;
  storeName: string | undefined;
}

function HomeMetricsBody({
  data,
  failureMessage,
  storeName,
}: HomeMetricsBodyProps) {
  if (failureMessage !== null) {
    return <HomeNotice heading={failureMessage} iconName="info" />;
  }

  if (data === undefined) {
    return (
      <>
        <div className={METRIC_GRID}>
          <span className="sr-only" role="status">
            {m.home_metrics_loading()}
          </span>
          <MetricCard loading />
          <MetricCard loading />
          <MetricCard loading />
        </div>
        <ConversationTrendLoading />
      </>
    );
  }

  const emptyNotice = readEmptyNotice(data, storeName);

  if (emptyNotice !== null) {
    return (
      <HomeNotice
        body={emptyNotice.body}
        heading={emptyNotice.heading}
        note={emptyNotice.note}
      />
    );
  }

  const { aiResolutionRate, conversations, humanBackstopRate } = data.metrics;

  return (
    <>
      <div className={METRIC_GRID}>
        <MetricCard
          delta={formatCountChange(conversations.change)}
          deltaTone={readChangeTone(conversations.change)}
          help={{
            label: m.home_metric_help_label({
              metric: m.home_metric_conversations_label(),
            }),
            content: m.home_metric_conversations_help(),
          }}
          label={m.home_metric_conversations_label()}
          value={formatCount(conversations.value)}
        />
        <MetricCard
          delta={formatRateChange(aiResolutionRate.change)}
          deltaTone={readChangeTone(aiResolutionRate.change)}
          help={{
            label: m.home_metric_help_label({
              metric: m.home_metric_ai_resolution_label(),
            }),
            content: m.home_metric_ai_resolution_help(),
          }}
          label={m.home_metric_ai_resolution_label()}
          value={formatRate(aiResolutionRate.value)}
        />
        <MetricCard
          delta={formatRateChange(humanBackstopRate.change)}
          deltaTone={readInvertedChangeTone(humanBackstopRate.change)}
          help={{
            label: m.home_metric_help_label({
              metric: m.home_metric_human_backstop_label(),
            }),
            content: m.home_metric_human_backstop_help(),
          }}
          label={m.home_metric_human_backstop_label()}
          value={formatRate(humanBackstopRate.value)}
        />
      </div>
      {/* 周期读响应里的，不读界面上的：切了周期但新数据还没到时，
          图上画的仍是旧那批，聚合口径必须跟着数据走。 */}
      <ConversationTrend period={data.period} trend={data.trend} />
    </>
  );
}

export interface HomePageProps {
  onPeriodChange: (period: Period) => void;
  period: Period;
}

/** 后台默认页：三张指标卡加一张按日趋势图，周期由路由 search 参数决定。 */
export function HomePage({ onPeriodChange, period }: HomePageProps) {
  const storeConnection = useAtomValue(storeConnectionAtom);
  const metrics = useAtomValue(homeMetricsAtom(period));
  const tenant = storeConnection.data;
  const updatedAt = metrics.dataUpdatedAt;

  return (
    <div className="flex flex-col gap-5.5">
      <PageHeader
        actions={
          <div className="flex items-center gap-2.5">
            {tenant?.status === "ready" && updatedAt > 0 && (
              <StatusMark tone="ready">
                {m.home_updated_at({
                  time: formatWorkspaceTime(updatedAt, tenant.timezone),
                })}
              </StatusMark>
            )}
            <SegmentedControl
              hideLabel
              label={m.home_period_label()}
              onChange={(value) => {
                const selected = periodSchema.safeParse(value);

                if (selected.success) {
                  onPeriodChange(selected.data);
                }
              }}
              options={PERIOD_OPTIONS}
              value={period}
            />
          </div>
        }
        meta={readStoreConnectionFailureMessage(storeConnection.error)}
        title={m.home_page_title()}
      />
      <HomeMetricsBody
        data={metrics.data}
        failureMessage={readHomeMetricsFailureMessage(metrics.error)}
        storeName={tenant?.storeName}
      />
    </div>
  );
}
